import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import Icon from '../../../_shared/components/Icon';
import BaseModal from '../../../_shared/components/BaseModal';
import theme from '../../../_shared/themes/tradeRev/theme';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import useEscModalTracking from '../../../_shared/hooks/useEscModalTracking';
import { MediaType } from '../../../_shared/types/media';
import ROUTES from '../../Routes/constants';
import { BidSource } from '../../../_shared/types/auction';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties,
  getVinLaunchProperties
} from '../../../_shared/hooks/useAnalytics';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

import {
  Header,
  Year,
  Make,
  Model,
  Esc,
  CloseButton,
  Grid,
  Content,
  OverlayCaption,
  OverlayMediaCount,
  GalleryArrow,
  Image,
  Video,
  ThumbnailSection,
  ThumbnailHeader,
  ThumbnailSubHeader,
  ThumbnailGrid,
  ThumbnailWrapper,
  ThumbnailShadow,
  ThumbnailDisclosureCount,
  ThumbnailImage,
  ThumbnailVideo,
  DamageMarker,
  DamageMarkerContent,
  DamageImage,
  DamageMarkerLabel,
  DamagePreviewThumbnail,
  PlayButtonOverlay,
  PlayButton
} from './Styles';

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeOverlay: PropTypes.func.isRequired,
  mediaList: PropTypes.objectOf(PropTypes.any).isRequired,
  tradeDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  preSelectedPhotoId: PropTypes.number
};

const defaultProps = {
  preSelectedPhotoId: undefined
};

const MediaGallery = ({
  isOpen,
  closeOverlay,
  mediaList,
  tradeDetails,
  preSelectedPhotoId
}) => {
  const [videos, setVideos] = useState(null);
  const [disclosurePhotos, setDisclosurePhotos] = useState(null);
  const [nonDamagePhotos, setNonDamagePhotos] = useState(null);
  const [media, setMedia] = useState(null);
  const [activeMediaIndex, setActiveMediaIndex] = useState(null);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [activeDamageRelatesToIndex, setActiveDamageRelatesToIndex] = useState(
    null
  );
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);
  const [numThumbClick, setNumThumbClick] = useState(0);
  const mediaContentRef = useRef();
  const mediaContainerRef = useRef();
  const currentMediaRef = useRef();
  const track = useAnalytics();

  useEscModalTracking('VDP-Multimedia', isOpen, setNumThumbClick);

  /**
   * stop mousedown events from bubbling up within the MediaGallery Modal
   * to prevent OMP Search UI useOutsideClickListener from closing the VDP when
   * MediaGallery modal open
   */
  const modalContentRef = useCallback(modalContentNode => {
    function preventPropagation(event) {
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    if (modalContentNode) {
      modalContentNode.addEventListener('mousedown', preventPropagation);
    }
  }, []);

  /**
   * Returns the image display name for the Overlay
   */
  const getDisplayName = mediaItem =>
    mediaItem.type === MediaType.image
      ? mediaItem.displayName || mediaItem.imageLocation?.displayName
      : '';

  /**
   * Arrow controls
   */
  const goPrev = () => {
    setMediaLoaded(false);
    if (activeMediaIndex - 1 < 0) setActiveMediaIndex(media?.length - 1);
    else setActiveMediaIndex(activeMediaIndex - 1);
  };

  const goNext = () => {
    setMediaLoaded(false);
    if (activeMediaIndex + 1 >= media?.length) setActiveMediaIndex(0);
    else setActiveMediaIndex(activeMediaIndex + 1);
  };

  const getDamageMarkerPosition = damageMarker => {
    if (currentMediaRef?.current && damageMarker) {
      const { width, height } = currentMediaRef.current.getBoundingClientRect();
      const x = damageMarker?.damageLocationCoordinates?.x * width;
      const y = damageMarker?.damageLocationCoordinates?.y * height;

      if (x && y) {
        return {
          top: `${y}px`,
          left: `${x}px`
        };
      }
    }

    return {};
  };

  const findDisclosurePhotoByExternalUrl = externalUrl =>
    disclosurePhotos.findIndex(photo => photo.externalUrl === externalUrl);

  const trackMultimediaNavigation = source => {
    const location = window.location.href.includes(ROUTES.BUYING)
      ? BidSource.MINI_VDP
      : BidSource.VDP_OVERLAY;
    track(MixpanelConfig.names.multimediaNavigation, {
      trade_id: tradeDetails?.id,
      source: `${location} ${source}`
    });
  };

  const onDamageMarkerClick = damageMarker => {
    const index = findDisclosurePhotoByExternalUrl(damageMarker?.externalUrl);
    if (index >= 0) {
      setActiveMediaIndex(index);
      trackMultimediaNavigation(MixpanelConfig.imageOrigin.Disclosure);
    }
  };

  const onThumbnailClick = index => {
    if (index === activeMediaIndex) {
      return;
    }

    setMediaLoaded(false);
    setActiveMediaIndex(index);

    // Only track this mixpanel event the first time the user clicks a thumbnail
    if (numThumbClick === 0) {
      if (isOmpPlatform()) {
        track(SegmentConfig.names.vdpClickMultimediaThumbnail, {
          ...getSellerProperties(tradeDetails),
          ...getVinBasicProperties(tradeDetails),
          ...getVinLaunchProperties(tradeDetails)
        });
      } else {
        track(MixpanelConfig.names.clickThumbnail, {
          trade_id: tradeDetails?.id,
          auction_id: tradeDetails?.auctionSummary?.id
        });
      }
      setNumThumbClick(numThumbClick + 1);
    }
  };

  /**
   * Handles enlargement of the image depending on the window ratio
   */
  const resizeFluidContainer = () => {
    if (
      !mediaContentRef?.current ||
      !mediaContainerRef?.current ||
      !currentMediaRef?.current
    ) {
      return;
    }

    mediaContainerRef.current.style.width = 'auto';
    mediaContainerRef.current.style.height = 'auto';

    const contentRect = mediaContentRef.current.getBoundingClientRect();
    const currentMediaRect = currentMediaRef.current.getBoundingClientRect();
    const contentRatio = contentRect.width / contentRect.height;
    const currentMediaRatio = currentMediaRect.width / currentMediaRect.height;

    if (contentRatio > currentMediaRatio) {
      mediaContainerRef.current.style.width = 'auto';
      mediaContainerRef.current.style.height = '100%';
    } else {
      mediaContainerRef.current.style.width = '100%';
      mediaContainerRef.current.style.height = 'auto';
    }
  };

  const playVideo = () => {
    const video = currentMediaRef?.current;
    if (!video) {
      return;
    }

    let isSeeking = false;
    setShowPlayOverlay(false);
    video.play();
    video.onseeking = () => {
      isSeeking = true;
    };

    video.onseeked = () => {
      isSeeking = false;
    };

    video.onpause = () => {
      setTimeout(() => {
        if (!isSeeking) {
          setShowPlayOverlay(true);
        }
      }, 30);
    };
  };

  const trackVideoPlayed = () => {
    track(SegmentConfig.names.vdpVideoPlayed, {
      ...getSellerProperties(tradeDetails),
      ...getVinBasicProperties(tradeDetails),
      ...getVinLaunchProperties(tradeDetails),
      is_fullscreen: !!document.fullscreenElement,
      total_overview_images: mediaList?.meta?.count?.photos,
      total_damage_images: mediaList?.meta?.count?.damages,
      total_videos: mediaList?.meta?.count?.videos
    });
  };

  /**
   * Resize and keydown events
   */
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'ArrowLeft') {
        goPrev();
      } else if (event.key === 'ArrowRight') {
        goNext();
      }
    };

    window.addEventListener('resize', resizeFluidContainer);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('resize', resizeFluidContainer);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [activeMediaIndex]); /* eslint-disable-line react-hooks/exhaustive-deps */

  /**
   * On Media item change
   */
  useEffect(() => {
    const findNonDamagePhotoByExternalUrl = externalUrl =>
      nonDamagePhotos.findIndex(photo => {
        if (photo.damageMetaData) {
          return !!photo.damageMetaData.find(
            damagePhoto => damagePhoto.externalUrl === externalUrl
          );
        }

        return false;
      });

    if (isOpen && activeMediaIndex !== null && mediaLoaded) {
      resizeFluidContainer();

      const nonDamagePhotoIndex = findNonDamagePhotoByExternalUrl(
        media[activeMediaIndex]?.externalUrl
      );

      if (nonDamagePhotoIndex >= 0) {
        setActiveDamageRelatesToIndex(
          disclosurePhotos?.length + nonDamagePhotoIndex
        );
      } else {
        setActiveDamageRelatesToIndex(null);
      }
    }
  }, [
    isOpen,
    activeMediaIndex,
    mediaLoaded,
    disclosurePhotos?.length,
    nonDamagePhotos,
    media
  ]);

  /**
   * Combine media
   */
  useEffect(() => {
    const findPhotoIndexByPhotoId = (combinedMedia, id) =>
      combinedMedia ? combinedMedia.findIndex(photo => photo.id === id) : 0;

    if (!nonDamagePhotos?.length || !isOpen) {
      return;
    }

    const combinedMedia = [...disclosurePhotos, ...nonDamagePhotos, ...videos];
    if (combinedMedia.length > 0) {
      setMedia(combinedMedia);

      if (preSelectedPhotoId >= 0) {
        setActiveMediaIndex(
          findPhotoIndexByPhotoId(combinedMedia, preSelectedPhotoId)
        );
      } else {
        setActiveMediaIndex(0);
      }
    }
  }, [disclosurePhotos, nonDamagePhotos, videos, isOpen, preSelectedPhotoId]);

  useEffect(() => {
    if (isOpen) {
      setMediaLoaded(false);
    } else {
      // Reset the number of tracked thumbnail clicks when the user closes Multimedia overlay
      setNumThumbClick(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (mediaList) {
      // TODO: Revist this, and figure out why media gets updated on every live bid
      // AB#206987
      if (!isEqual(videos, mediaList.videos)) {
        setVideos(mediaList.videos);
      }
      if (!isEqual(disclosurePhotos, mediaList.meta.disclosurePhotos)) {
        setDisclosurePhotos(mediaList.meta.disclosurePhotos);
      }
      if (!isEqual(nonDamagePhotos, mediaList.meta.nonDamagePhotos)) {
        setNonDamagePhotos(mediaList.meta.nonDamagePhotos);
      }
    }
  }, [mediaList]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BaseModal
      isOpen={isOpen}
      bodyOpenClassName="ReactModal__FullScreen"
      padding="0"
      borderRadius="0"
      isFullScreen
      isClickEscClose
      onRequestClose={closeOverlay}
      testId="vdp-multimedia-overlay"
      setRef={modalContentRef}
    >
      <Header>
        <Header.Title>
          <Year>{tradeDetails?.year}</Year>
          <Make className="notranslate">{tradeDetails?.manufacturerName}</Make>
          <Model className="notranslate">{tradeDetails?.modelName}</Model>
        </Header.Title>
        <Header.Controls>
          <FormattedMessage
            id="vdp.multimedia.header.esc"
            defaultMessage="Press the <esc>esc</esc> key to"
            values={{
              esc: chunks => <Esc>{chunks}</Esc>
            }}
          />
          <CloseButton onClick={closeOverlay}>
            <FormattedMessage id="vdp.close" />
            <CloseButton.Icon />
          </CloseButton>
        </Header.Controls>
      </Header>
      <Grid>
        <Grid.Thumbnails>
          <ThumbnailShadow />
          <ThumbnailHeader>
            {/* Multimedia */}
            <FormattedMessage id="vdp.multimedia.title" />
          </ThumbnailHeader>
          {disclosurePhotos?.length > 0 && (
            <ThumbnailSection>
              <ThumbnailSubHeader>
                {/* Disclosure images */}
                {disclosurePhotos.length}{' '}
                <FormattedMessage id="vdp.multimedia.disclosureImages" />
              </ThumbnailSubHeader>
              <ThumbnailGrid>
                {disclosurePhotos.map((photo, i) => (
                  <ThumbnailWrapper
                    key={photo.id}
                    onClick={() => {
                      onThumbnailClick(i);
                    }}
                  >
                    <ThumbnailImage
                      src={photo.previewUrl}
                      variant={activeMediaIndex === i && 'active'}
                    />
                  </ThumbnailWrapper>
                ))}
              </ThumbnailGrid>
            </ThumbnailSection>
          )}
          {nonDamagePhotos?.length > 0 && (
            <ThumbnailSection>
              <ThumbnailSubHeader>
                {/* Overview images */}
                {nonDamagePhotos.length}{' '}
                <FormattedMessage id="vdp.multimedia.overviewImages" />
              </ThumbnailSubHeader>
              <ThumbnailGrid>
                {nonDamagePhotos.map((photo, i) => (
                  <ThumbnailWrapper
                    key={photo.id}
                    onClick={() => {
                      onThumbnailClick(disclosurePhotos?.length + i);
                    }}
                  >
                    {photo.damageMetaData &&
                      photo.damageMetaData.length > 0 && (
                        <ThumbnailDisclosureCount>
                          {photo.damageMetaData.length}
                        </ThumbnailDisclosureCount>
                      )}
                    <ThumbnailImage
                      src={photo.previewUrl}
                      variant={
                        activeMediaIndex === disclosurePhotos?.length + i &&
                        'active'
                      }
                    />
                  </ThumbnailWrapper>
                ))}
              </ThumbnailGrid>
            </ThumbnailSection>
          )}
          {videos?.length > 0 && (
            <ThumbnailSection>
              <ThumbnailSubHeader>
                {/* Video */}
                {videos.length} <FormattedMessage id="vdp.multimedia.video" />
              </ThumbnailSubHeader>
              <ThumbnailGrid>
                {videos.map((video, i) => (
                  <ThumbnailWrapper
                    key={video.id}
                    onClick={() => {
                      onThumbnailClick(
                        disclosurePhotos?.length + nonDamagePhotos?.length + i
                      );
                    }}
                  >
                    {/* TODO: Rework this, and display an image preview */}
                    <ThumbnailVideo
                      src={video.externalUrl}
                      variant={
                        activeMediaIndex ===
                          disclosurePhotos?.length +
                            nonDamagePhotos?.length +
                            i && 'active'
                      }
                    />
                  </ThumbnailWrapper>
                ))}
              </ThumbnailGrid>
            </ThumbnailSection>
          )}
        </Grid.Thumbnails>
        <Grid.Content>
          {media && activeMediaIndex >= 0 && (
            <>
              {media?.length > 1 && (
                <>
                  <GalleryArrow
                    variant="left"
                    onClick={goPrev}
                    data-testid="multimediaLeft"
                  >
                    <GalleryArrow.Left />
                  </GalleryArrow>
                  <GalleryArrow
                    variant="right"
                    onClick={goNext}
                    data-testid="multimediaRight"
                  >
                    <GalleryArrow.Right />
                  </GalleryArrow>
                </>
              )}

              <Content ref={mediaContentRef}>
                <Content.FluidContainer ref={mediaContainerRef}>
                  <Content.Overlay>
                    <OverlayCaption>
                      {activeDamageRelatesToIndex && (
                        <DamagePreviewThumbnail
                          src={media[activeDamageRelatesToIndex]?.externalUrl}
                          onClick={() => {
                            setActiveMediaIndex(activeDamageRelatesToIndex);
                            trackMultimediaNavigation(
                              MixpanelConfig.imageOrigin.Overview
                            );
                          }}
                        />
                      )}
                      {getDisplayName(media[activeMediaIndex])}
                    </OverlayCaption>
                    <OverlayMediaCount>{`${activeMediaIndex + 1} / ${
                      media.length
                    }`}</OverlayMediaCount>
                  </Content.Overlay>
                  {mediaLoaded &&
                    media[activeMediaIndex]?.damageMetaData?.map(
                      damageMarker => (
                        <DamageMarker
                          key={damageMarker.id}
                          position={getDamageMarkerPosition(damageMarker)}
                          data-tip
                          data-for={damageMarker.id}
                          onClick={() => onDamageMarkerClick(damageMarker)}
                        >
                          <ReactTooltip
                            id={damageMarker.id}
                            backgroundColor={theme.COLORS.BASE}
                            textColor={theme.COLORS.BASE_INVERTED}
                            effect="solid"
                          >
                            <DamageMarkerContent>
                              <DamageImage
                                key={damageMarker.id}
                                src={damageMarker.externalUrl}
                              />
                              <DamageMarkerLabel>
                                {damageMarker.displayName}
                              </DamageMarkerLabel>
                            </DamageMarkerContent>
                          </ReactTooltip>
                        </DamageMarker>
                      )
                    )}
                  {media[activeMediaIndex]?.type === MediaType.image ? (
                    <Image
                      src={media[activeMediaIndex].externalUrl}
                      alt={getDisplayName(media[activeMediaIndex])}
                      title={getDisplayName(media[activeMediaIndex])}
                      ref={currentMediaRef}
                      onLoad={() => {
                        setMediaLoaded(true);
                      }}
                      data-testid={getDisplayName(media[activeMediaIndex])}
                    />
                  ) : (
                    <>
                      <PlayButtonOverlay
                        show={showPlayOverlay}
                        onClick={playVideo}
                      >
                        <PlayButton>
                          <Icon icon="TR5-Play_button" />
                        </PlayButton>
                      </PlayButtonOverlay>
                      <Video
                        controls
                        src={media[activeMediaIndex].externalUrl}
                        ref={currentMediaRef}
                        onPlay={trackVideoPlayed}
                        onLoadedData={() => {
                          setMediaLoaded(true);
                        }}
                      />
                    </>
                  )}
                </Content.FluidContainer>
              </Content>
            </>
          )}
        </Grid.Content>
      </Grid>
    </BaseModal>
  );
};

MediaGallery.propTypes = propTypes;
MediaGallery.defaultProps = defaultProps;

export default MediaGallery;
